import { inject } from 'mobx-react';
import * as React from 'react';

import { merge, RouteStore } from 'bernie-core';

import { FormOptions, LodgingForm } from '@shared-ui/lodging-form';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';

import { UitkSpacing } from 'uitk-react-spacing';

import { ActionProvider, OnChangeFn, OnErrorFn, OnSubmitFn } from './action-provider';
import { alwaysIgnoredKeys } from 'components/common/search-form';
import { useFormInputs } from './use-form-inputs';

interface SharedUiFormWrapperProps {
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
  onChange?: OnChangeFn;
  onSubmit?: OnSubmitFn;
  onError?: OnErrorFn;
  action?: string;
  formRef?: React.RefObject<HTMLFormElement>;
}

export const HiddenFields: React.FC<{ searchCriteria: SearchCriteriaModel }> = ({ searchCriteria }) => {
  const [excludedFields, setExcludedFields] = React.useState(['latLong', 'regionId', 'x_pwa']);

  React.useEffect(() => {
    setExcludedFields([]);
  }, []);

  const hiddenFields = Object.keys(
    merge.withoutKeys([...alwaysIgnoredKeys, ...excludedFields, 'startDate', 'endDate', 'rooms']).merge({}, searchCriteria)
  );

  return (
    <>
      {hiddenFields.map(field => {
        const value = searchCriteria[field] || '';
        const disabled = value === '' || (typeof value?.length === 'number' && value.length === 0);

        return <input key={`hidden-field-${field}`} type="hidden" name={field} value={value} disabled={disabled} />;
      })}
    </>
  );
};

export const SharedUiFormWrapper: React.FC<SharedUiFormWrapperProps> = inject('currentHotel')(
  ({ currentHotel, onChange, onSubmit, onError, action, children, formRef }) => {
    const { hotelId, searchCriteria } = currentHotel || {};

    const formData: FormOptions['data'] = React.useMemo(
      () => ({
        method: 'GET',
        action,
        id: 'pdp-search-form',
      }),
      [action]
    );
    const inputs = useFormInputs(hotelId, searchCriteria);

    // Hidden fields need a few extra values for noJS
    const hiddenFieldsSearchCriteria = searchCriteria?.clone();

    /* istanbul ignore next */
    if (typeof window === 'undefined' && currentHotel && hiddenFieldsSearchCriteria) {
      Object.assign(hiddenFieldsSearchCriteria, currentHotel.hsrQueryValues);
    }

    if (!hotelId || !searchCriteria) {
      return null;
    }

    return (
      <UitkSpacing margin={{ blockend: 'two' }}>
        <div>
          <ActionProvider formAction={action} searchCriteria={searchCriteria} onChange={onChange} onSubmit={onSubmit} onError={onError}>
            <LodgingForm data={formData} inputs={inputs} formRef={formRef}>
              {children}
              <HiddenFields searchCriteria={hiddenFieldsSearchCriteria} />
            </LodgingForm>
          </ActionProvider>
        </div>
      </UitkSpacing>
    );
  }
);
